import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import * as ACData from 'adaptivecards-templating';
import {
  AdaptiveCardType,
  CardData,
  CardElement,
  ComboCard,
  ContainerId,
} from '../../ccf-live-copilot-helper';
import { allAdaptiveCardSchemas } from '../../ccf-copilot-schemas';

export const CCF_LIVE_COPILOT_KEY = 'liveCopilot';

export interface AAHConfig {
  generativeResponseTone: string;
  expertCopilotURL: string;
  expertCopilotClientKey: string;
  expertCopilotClientSecret: string;
  botPersona: string;
  userPersona: string;
  path: string;
  associatedFile: string;
  kBAnswersPath: string;
  salesOffersPath: string;
  compliancePath: string;
  enableSentiment: boolean;
  sentimentResponseLength: number;
  realTimeSummary: boolean;
  realTimeSummaryResponseLength: number;
  finalSummary: boolean;
  finalSummaryResponseLength: number;
  behavioralGuidance: boolean;
  behavioralGuidanceResponseLength: number;
  behevioralGuidanceNumberOfResponse: number;
  enableExpertCopilotIntegration: boolean;
  nextBestResponse: boolean;
  nextBestResponseLength: number;
  nextBestNumberOfResponse: number;
  knowledgeBaseArticles: boolean;
  knowledgeBaseArticlesResponseLength: number;
  knowledgeBaseWebLinks: boolean;
  knowledgeBaseMaximumWebLinks: number;
  knowledgeBaseImages: boolean;
  knowledgeBaseMaximumImages: number;
  knowledgeBaseProcessSteps: boolean;
  knowledgeBaseMaximumSteps: number;
  salesOffers: boolean;
  compliance: boolean;
  threshold: number;
  threshold_sales: number;
  threshold_compliance: number;
  language: string;
  backAndForthLiveConvo: boolean;
	backAndForthLiveConvoLength: number;
  demoType: string;
}

export enum AgentCopilotContent {
  ADAPTIVE_CARD = 'AdaptiveCard',
  RT_SUMMARY = 'rtSummary',
  NEXT_BEST_RESPONSE = 'nextBestResponse',
  NEXT_BEST_RESPONSE_VOICE = 'nextBestResponse_Voice',
  NEXT_BEST_RESPONSE_LLM = 'nextBestResponseLLM',
  NEXT_BEST_RESPONSE_EXPERT = 'nextBestResponseExpert',
  KB_ANSWERS = 'kbAnswers',
  SENTIMENT = 'sentimentAndReason',
  KB_LINKS = 'kbLinks',
  KB_IMAGES = 'kbImages',
  TEXT = 'Text',
  FINAL_SUMMARY = 'finalSummary',
  FINAL_SUMMARY_NOTES = 'finalSummaryNotes',
  KB_ANSWERS_VOICE = 'kbAnswers_Voice',
  KB_IMAGES_VOICE = 'kbImages_Voice',
  ENLIGHTEN_AGENT_COPILOT = 'Enlighten Agent Copilot',
  AGENT_COPILOT = 'agentCopilot',
  COPILOT_REQUEST_STATUS = 'currentRequestState',
  KB_COMBO = 'kbCombo',
  KB_COMBO_VOICE = 'kbCombo_Voice',
  KB_COMBO_EMAIL = 'kbCombo_Email',
  SALES_OFFER = 'salesOffer',
  SALES_OFFER_VOICE = 'salesOffer_Voice',
  TRANSFER_SUMMARY = 'transferSummary',
  COMPLIANCE_CARD = 'compliance',
  COMPLIANCE_CARD_VOICE = 'compliance_Voice',
  CONVERSATION_ASSISTANCE = 'conversationAssistance',
  INTENT_ASSISTANCE = 'intentAndAssistance',
  JOURNEY_SUMMARY = 'journeySummary',
  AGENT_TRANSFER = 'agentTransfer',
  AUTOPILOT_TRANSFER = 'autopilotTransfer',
  INTENT_COMPLETED = 'intentSuccess',
  INTENT_INPROGRESS = 'intentInprogress',
  COMPLIANCE = 'compliance',
  COMPLIANCE_VOICE = 'compliance_Voice',
  EMAIL_RESPONSE_SUGGESTED = 'emailResponseSuggested',
  EMAIL_RESPONSE_GENERATED = 'emailResponseGenerated',
  EMAIL_RESPONSE_SUGGESTED_LIVE = 'emailResponseSuggestedLive',
  EMAIL_RESPONSE_GENERATED_LIVE = 'emailResponseGeneratedLive',
  FEEDBACK = 'feedback',
  BEHAVIORAL_GUIDANCE = 'behavioralGuidance',
}

export interface CopilotSummary {
  summary: string;
}

/**
 * Type for icons data
 */
export type copilotAdaptiveCardIcon = {
  [key: string]: string;
};

export interface CopilotAdaptiveCard {
  type: string;
  version: string;
  body: any;
  $schema: string;
}

export interface CopilotNextBestResponse {
  nextBestResponses: {
    type: string;
    bestResponse: string[];
  };
}

export interface CopilotSentimentCardData {
  description: string;
  icon: string;
  sentiment: string;
}

export interface CopilotRequestStatus {
  contactId: number;
  utteranceId: string;
  currentState: string;
}

export interface CopilotElement {
  contentType:
    | AgentCopilotContent.RT_SUMMARY
    | AgentCopilotContent.NEXT_BEST_RESPONSE
    | AgentCopilotContent.NEXT_BEST_RESPONSE_LLM
    | AgentCopilotContent.NEXT_BEST_RESPONSE_EXPERT
    | AgentCopilotContent.NEXT_BEST_RESPONSE_VOICE
    | AgentCopilotContent.SENTIMENT
    | AgentCopilotContent.KB_IMAGES
    | AgentCopilotContent.KB_LINKS
    | AgentCopilotContent.KB_ANSWERS
    | AgentCopilotContent.FINAL_SUMMARY
    | AgentCopilotContent.FINAL_SUMMARY_NOTES
    | AgentCopilotContent.KB_ANSWERS_VOICE
    | AgentCopilotContent.KB_IMAGES_VOICE
    | AgentCopilotContent.COPILOT_REQUEST_STATUS
    | AgentCopilotContent.KB_COMBO
    | AgentCopilotContent.KB_COMBO_VOICE
    | AgentCopilotContent.KB_COMBO_EMAIL
    | AgentCopilotContent.COMPLIANCE
    | AgentCopilotContent.SALES_OFFER
    | AgentCopilotContent.SALES_OFFER_VOICE
    | AgentCopilotContent.COMPLIANCE_VOICE
    | AgentCopilotContent.CONVERSATION_ASSISTANCE
    | AgentCopilotContent.JOURNEY_SUMMARY
    | AgentCopilotContent.EMAIL_RESPONSE_GENERATED
    | AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED
    | AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED_LIVE
    | AgentCopilotContent.EMAIL_RESPONSE_GENERATED_LIVE
    | AgentCopilotContent.INTENT_COMPLETED
    | AgentCopilotContent.AGENT_TRANSFER
    | AgentCopilotContent.AUTOPILOT_TRANSFER
    | AgentCopilotContent.FEEDBACK;

  content?:
    | CopilotSummary
    | CopilotAdaptiveCard
    | CopilotNextBestResponse
    | CopilotSentimentCardData
    | CopilotRequestStatus
    | any;

  cardType: AgentCopilotContent.ADAPTIVE_CARD | AgentCopilotContent.TEXT;

  objectId: string;
}

export interface CopilotMessageData {
  agentAssistSource: string;
  contactId: string;
  acpAppElements: CopilotElement[];
  utcTimestamp: Date;
  responseSent: string;
  isResponseInserted: boolean;
  isResponseSent: boolean;
  isJourneySummaryExpanded: boolean;
  conversationTopics?: string[];
  isFeedbackSaved? : boolean;
  isEditorActionPerformed?: boolean;
}

/**
 * type for collection of Copilot message data for each case Id.
 */
export type CcfCopilotData = {
  [caseId: string]: CopilotMessageData;
};

export interface CcfLiveCopilotState {
  adaptiveCardsData: CcfCopilotData;
  isSent: { clicked: boolean; message: string };
  isInsert: { clicked: boolean; message: string };
  finalSummaryNotes: FinalSummary;
  copilotConfig?: AAHConfig;
  isLiveCopilotEnabled: boolean;
  isRequestProcessing: boolean;
  agentSearchTopic: string;
  isJourneySummaryExpanded?: boolean;
  botAssistValue1?: string;
  botAssistValue2?: string;
  botAssistValue3?: string;
  botAssistValue4?: string;
  botAssistValue5?: string;
  audioTranscript?:string;
}

export interface FinalSummary{
  summary: string;
  resolved: boolean;
  intents: string;
  actions: string;
  outcomes: string;
  entities: string;
}

export const initialCcfLiveCopilotState: CcfLiveCopilotState = {
  adaptiveCardsData: {
    '0': {
      contactId: '0',
      agentAssistSource: '',
      utcTimestamp: new Date('2024-04-08T05:57:47.300Z'),
      acpAppElements: [],
      responseSent: '',
      isResponseInserted: false,
      isResponseSent: false,
      isJourneySummaryExpanded: false,
    },
    '201251606723': {
      contactId: '201251606723',
      agentAssistSource: 'Illuminate',
      utcTimestamp: new Date('2024-04-08T11:45:01.788Z'),
      acpAppElements: [
        {
          contentType: AgentCopilotContent.SENTIMENT,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: 'de5a80dd-8e15-4fdc-8ebb-08f4263e32e1_sentimentAndReason',
          content: {
            type: 'AdaptiveCard',
            body: [
              {
                type: 'ColumnSet',
                columns: [
                  {
                    type: 'Column',
                    items: [
                      {
                        type: 'Image',
                        url: 'data:image/svg+xml;base64,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',
                        size: 'Small',
                      },
                    ],
                    width: 'auto',
                  },
                  {
                    type: 'Column',
                    items: [
                      {
                        type: 'TextBlock',
                        weight: 'Bolder',
                        text: 'Customer Sentiment is Neutral',
                        wrap: true,
                        size: 'small',
                        isSubtle: true,
                      },
                      {
                        type: 'TextBlock',
                        spacing: 'None',
                        text: "The customer's queries are straightforward and do not indicate any strong emotion.",
                        isSubtle: true,
                        textAlignment: 'justified',
                        wrap: true,
                        horizontalAlignment: 'Left',
                        size: 'small',
                        weight: 'Default',
                      },
                    ],
                    width: 'stretch',
                  },
                ],
              },
            ],
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.6',
          },
        },
        {
          contentType: AgentCopilotContent.KB_COMBO,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: 'de5a80dd-8e15-4fdc-8ebb-08f4263e32e1_kbAnswers',
          content: {
            type: 'AdaptiveCard',
            body: [
              {
                type: 'Container',
                id: 'kbAnswers',
                items: [
                  {
                    type: 'TextBlock',
                    size: 'small',
                    weight: 'Bolder',
                    text: '',
                    isSubtle: true,
                  },
                  {
                    type: 'TextBlock',
                    text: "A washing machine is a household appliance used for cleaning clothes.\n\nIt allows users to select different wash cycles such as Normal, Delicate, Heavy Duty, Rinse, Spin, or Clean Washer.\n\nMore information can be found in the Owner's Manual.",
                    weight: 'Default',
                    size: 'small',
                    isSubtle: true,
                    wrap: true,
                  },
                  {
                    type: 'ColumnSet',
                    columns: [
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Insert Article',
                              data: {
                                name: 'insertArticle',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'stretch',
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Send Article',
                              data: {
                                name: 'sendArticle',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'auto',
                      },
                    ],
                    separator: true,
                  },
                ],
              },
              {
                type: 'Container',
                id: 'kbLinks',
                isVisible: true,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'webLinksTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,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',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Web Links (3)',
                            wrap: true,
                            weight: 'Bolder',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openWebLinksContainer',
                      },
                    },
                    separator: true,
                  },
                  {
                    type: 'Container',
                    id: 'webLinksContainer',
                    isVisible: false,
                    items: [
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Cycle Selection - Stacked Laundry Center',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 0,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 0,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Cycle Selection - Stacked Laundry Center',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 1,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 1,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Washing Machine Comparison Table',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 2,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 2,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                    ],
                  },
                ],
                spacing: 'Medium',
              },
              {
                type: 'Container',
                id: 'kbImages',
                isVisible: false,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'imagesTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0LjY2NjMgMEgxLjMzMjc3QzAuNTk5MjY3IDAgLTAuMDAwNzMyNDIyIDAuNiAtMC4wMDA3MzI0MjIgMS4zMzM1VjE0LjY2N0MtMC4wMDA3MzI0MjIgMTUuNDAwNSAwLjU5OTI2NyAxNi4wMDA1IDEuMzMyNzcgMTYuMDAwNUgxNC42NjYzQzE1LjM5OTggMTYuMDAwNSAxNS45OTk4IDE1LjQwMDUgMTUuOTk5OCAxNC42NjdWMS4zMzM1QzE1Ljk5OTggMC42IDE1LjM5OTggMCAxNC42NjYzIDBaTTE0LjY2NjMgMS4zMzM1VjEzLjQ2N0wxMS43MzI4IDkuNjY3QzExLjM5OTMgOS4xMzM1IDEwLjk5OTMgOS4wNjcgMTAuNzMyOCA5LjA2N0MxMC4xOTkzIDkuMDY3IDkuNzk5MjcgOS40NjcgOS42NjYyNyA5LjYwMDVMOC45MzI3NyAxMC40MDA1TDUuMzk5MjcgNy4wMDA1QzUuMDY1NzcgNi41MzQgNC43MzI3NyA2LjQ2NyA0LjQ2NTc3IDYuNDY3QzMuOTMyMjcgNi40NjcgMy41MzIyNyA2Ljg2NyAzLjM5OTI3IDcuMDAwNUwxLjMzMjc3IDkuMDY3VjEuMzMzNUgxNC42NjYzWk0xMy45MzMzIDE0LjY2NjVINi41OTk3N0wxMC42NjYzIDEwLjQ2NjVMMTMuOTMzMyAxNC42NjY1Wk0xLjMzMzI3IDExTDQuMzk5NzcgNy44TDcuOTk5NzcgMTEuMzMzNUw0LjczMzI3IDE0LjY2N0gxLjMzMzI3VjExWiIgZmlsbD0iIzNGNUM2OSIvPgo8cGF0aCBkPSJNMTIuMzk5OCA0Ljg2NjVDMTIuMzk5OCA1Ljc4NzUgMTEuNjUzMyA2LjUzMyAxMC43MzMzIDYuNTMzQzkuODEzMjcgNi41MzMgOS4wNjY3NyA1Ljc4NjUgOS4wNjY3NyA0Ljg2NjVDOS4wNjY3NyAzLjk0NTUgOS44MTMyNyAzLjIgMTAuNzMzMyAzLjJDMTEuNjUzMyAzLjIgMTIuMzk5OCAzLjk0NjUgMTIuMzk5OCA0Ljg2NjVaIiBmaWxsPSIjM0Y1QzY5Ii8+Cjwvc3ZnPgo=',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Images (0)',
                            wrap: true,
                            weight: 'Bolder',
                            spacing: 'None',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openImagesContainer',
                      },
                    },
                    separator: true,
                  },
                  {
                    type: 'Container',
                    id: 'imagesContainer',
                    isVisible: false,
                    items: [],
                  },
                ],
                spacing: 'Medium',
              },
              {
                type: 'Container',
                id: 'kbProcessSteps',
                isVisible: false,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'processStepsTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,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',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Process Steps (0)',
                            wrap: true,
                            weight: 'Bolder',
                            spacing: 'None',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Insert Steps',
                              data: {
                                name: 'insertSteps',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'stretch',
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Send Steps',
                              data: {
                                name: 'sendSteps',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'auto',
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openProcessStepsContainer',
                      },
                    },
                    separator: true,
                  },
                ],
                spacing: 'Medium',
              },
            ],
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.6',
          },
        },
        {
          contentType: AgentCopilotContent.KB_COMBO,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: 'b4cff8a6-2136-4884-ab6e-1ac4f50d33af_kbAnswers',
          content: {
            type: 'AdaptiveCard',
            body: [
              {
                type: 'Container',
                id: 'kbAnswers',
                items: [
                  {
                    type: 'TextBlock',
                    size: 'small',
                    weight: 'Bolder',
                    text: '',
                    isSubtle: true,
                  },
                  {
                    type: 'TextBlock',
                    text: "A washing machine is a household appliance used for cleaning clothes.\n\nIt offers various wash cycles such as Normal, Delicate, Heavy Duty, Rinse, Spin, or Clean Washer.\n\nFor more information, refer to the Owner's Manual.",
                    weight: 'Default',
                    size: 'small',
                    isSubtle: true,
                    wrap: true,
                  },
                  {
                    type: 'ColumnSet',
                    columns: [
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Insert Article',
                              data: {
                                name: 'insertArticle',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'stretch',
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Send Article',
                              data: {
                                name: 'sendArticle',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'auto',
                      },
                    ],
                    separator: true,
                  },
                ],
              },
              {
                type: 'Container',
                id: 'kbLinks',
                isVisible: true,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'webLinksTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,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',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Web Links (3)',
                            wrap: true,
                            weight: 'Bolder',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openWebLinksContainer',
                      },
                    },
                    separator: true,
                  },
                  {
                    type: 'Container',
                    id: 'webLinksContainer',
                    isVisible: false,
                    items: [
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Cycle Selection - Stacked Laundry Center',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 0,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 0,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Cycle Selection - Stacked Laundry Center',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 1,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 1,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Washing Machine Comparison Table',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 2,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 2,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                    ],
                  },
                ],
                spacing: 'Medium',
              },
              {
                type: 'Container',
                id: 'kbImages',
                isVisible: false,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'imagesTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0LjY2NjMgMEgxLjMzMjc3QzAuNTk5MjY3IDAgLTAuMDAwNzMyNDIyIDAuNiAtMC4wMDA3MzI0MjIgMS4zMzM1VjE0LjY2N0MtMC4wMDA3MzI0MjIgMTUuNDAwNSAwLjU5OTI2NyAxNi4wMDA1IDEuMzMyNzcgMTYuMDAwNUgxNC42NjYzQzE1LjM5OTggMTYuMDAwNSAxNS45OTk4IDE1LjQwMDUgMTUuOTk5OCAxNC42NjdWMS4zMzM1QzE1Ljk5OTggMC42IDE1LjM5OTggMCAxNC42NjYzIDBaTTE0LjY2NjMgMS4zMzM1VjEzLjQ2N0wxMS43MzI4IDkuNjY3QzExLjM5OTMgOS4xMzM1IDEwLjk5OTMgOS4wNjcgMTAuNzMyOCA5LjA2N0MxMC4xOTkzIDkuMDY3IDkuNzk5MjcgOS40NjcgOS42NjYyNyA5LjYwMDVMOC45MzI3NyAxMC40MDA1TDUuMzk5MjcgNy4wMDA1QzUuMDY1NzcgNi41MzQgNC43MzI3NyA2LjQ2NyA0LjQ2NTc3IDYuNDY3QzMuOTMyMjcgNi40NjcgMy41MzIyNyA2Ljg2NyAzLjM5OTI3IDcuMDAwNUwxLjMzMjc3IDkuMDY3VjEuMzMzNUgxNC42NjYzWk0xMy45MzMzIDE0LjY2NjVINi41OTk3N0wxMC42NjYzIDEwLjQ2NjVMMTMuOTMzMyAxNC42NjY1Wk0xLjMzMzI3IDExTDQuMzk5NzcgNy44TDcuOTk5NzcgMTEuMzMzNUw0LjczMzI3IDE0LjY2N0gxLjMzMzI3VjExWiIgZmlsbD0iIzNGNUM2OSIvPgo8cGF0aCBkPSJNMTIuMzk5OCA0Ljg2NjVDMTIuMzk5OCA1Ljc4NzUgMTEuNjUzMyA2LjUzMyAxMC43MzMzIDYuNTMzQzkuODEzMjcgNi41MzMgOS4wNjY3NyA1Ljc4NjUgOS4wNjY3NyA0Ljg2NjVDOS4wNjY3NyAzLjk0NTUgOS44MTMyNyAzLjIgMTAuNzMzMyAzLjJDMTEuNjUzMyAzLjIgMTIuMzk5OCAzLjk0NjUgMTIuMzk5OCA0Ljg2NjVaIiBmaWxsPSIjM0Y1QzY5Ii8+Cjwvc3ZnPgo=',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Images (0)',
                            wrap: true,
                            weight: 'Bolder',
                            spacing: 'None',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openImagesContainer',
                      },
                    },
                    separator: true,
                  },
                  {
                    type: 'Container',
                    id: 'imagesContainer',
                    isVisible: false,
                    items: [],
                  },
                ],
                spacing: 'Medium',
              },
              {
                type: 'Container',
                id: 'kbProcessSteps',
                isVisible: false,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'processStepsTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,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',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Process Steps (0)',
                            wrap: true,
                            weight: 'Bolder',
                            spacing: 'None',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Insert Steps',
                              data: {
                                name: 'insertSteps',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'stretch',
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Send Steps',
                              data: {
                                name: 'sendSteps',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'auto',
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openProcessStepsContainer',
                      },
                    },
                    separator: true,
                  },
                ],
                spacing: 'Medium',
              },
            ],
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.6',
          },
        },
        {
          contentType: AgentCopilotContent.RT_SUMMARY,
          cardType: AgentCopilotContent.TEXT,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_rtSummary',
          content: {
            summary:
              'The customer is asking about the definition and use of a dishwasher and a washing machine.The customer is asking about the definition and use of a dishwasher and a washing machine.The customer is asking about the definition and use of a dishwasher and a washing machine.The customer is asking about the definition and use of a dishwasher and a washing machine.The customer is asking about the definition and use of a dishwasher and a washing machine.',
          },
        },
        {
          contentType: AgentCopilotContent.NEXT_BEST_RESPONSE,
          cardType: AgentCopilotContent.TEXT,
          content: {
            nextBestResponses: {
              type: 'nextBestResponse',
              bestResponse: [
                'Have you used the dryer for the first time? A new electric heating element may have an odor that will go away after a few cycles.',
                'Is the dryer located in an area where paint, stain, or varnish was recently used? Ventilate the area and avoid running the dryer while painting or refinishing near it.',
              ],
            },
          },
          objectId:
            '494fc732-f1be-466a-bfc5-2410a1e4f778_nextBestResponseExpert',
        },
        {
          contentType: AgentCopilotContent.KB_COMBO,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_kbAnswers',
          content: {
            type: 'AdaptiveCard',
            body: [
              {
                type: 'Container',
                id: 'kbAnswers',
                items: [
                  {
                    type: 'TextBlock',
                    size: 'small',
                    weight: 'Bolder',
                    text: '',
                    isSubtle: true,
                  },
                  {
                    type: 'TextBlock',
                    text: "A washing machine is a household appliance used for cleaning clothes.\n\nIt has different wash cycles such as Normal, Delicate, Heavy Duty, Rinse, Spin, or Clean Washer.\n\nFor more information, refer to the Owner's Manual.",
                    weight: 'Default',
                    size: 'small',
                    isSubtle: true,
                    wrap: true,
                  },
                  {
                    type: 'ColumnSet',
                    columns: [
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Insert Article',
                              data: {
                                name: 'insertArticle',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'stretch',
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Send Article',
                              data: {
                                name: 'sendArticle',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'auto',
                      },
                    ],
                    separator: true,
                  },
                ],
              },
              {
                type: 'Container',
                id: 'kbLinks',
                isVisible: true,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'webLinksTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,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',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Web Links (3)',
                            wrap: true,
                            weight: 'Bolder',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openWebLinksContainer',
                      },
                    },
                    separator: true,
                  },
                  {
                    type: 'Container',
                    id: 'webLinksContainer',
                    isVisible: false,
                    items: [
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Cycle Selection - Stacked Laundry Center',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 0,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 0,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Washer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Cycle Selection - Stacked Laundry Center',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 1,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 1,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Stacked_Laundry_Center/Dryer_questions_or_concerns/Control_Panel_Operation_(on_some_models)/27_Inch_Models/Cycle_Selection_-_Stacked_Laundry_Center',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                      {
                        type: 'ColumnSet',
                        columns: [
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'TextBlock',
                                text: 'Washing Machine Comparison Table',
                                wrap: true,
                                size: 'small',
                                weight: 'default',
                              },
                            ],
                          },
                          {
                            type: 'Column',
                            width: 'stretch',
                            items: [
                              {
                                type: 'ColumnSet',
                                columns: [
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik05LjYgMFYxLjZIMTMuMjhMOC4yNCA2LjY0TDkuMzYgNy43NkwxNC40IDIuNzJWNi40SDE2VjBIOS42WiIgZmlsbD0iIzNGNUM2OSIvPg0KPHBhdGggZD0iTTE0LjQgMTIuOEMxNC40IDEzLjY4IDEzLjY4IDE0LjQgMTIuOCAxNC40SDMuMkMyLjMyIDE0LjQgMS42IDEzLjY4IDEuNiAxMi44VjMuMkMxLjYgMi4zMiAyLjMyIDEuNiAzLjIgMS42SDhWMEgzLjJDMS40NCAwIDAgMS40NCAwIDMuMlYxMi44QzAgMTQuNTYgMS40NCAxNiAzLjIgMTZIMTIuOEMxNC41NiAxNiAxNiAxNC41NiAxNiAxMi44VjhIMTQuNFYxMi44WiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.OpenUrl',
                                          title: 'Open Link',
                                          url: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          data: {
                                            name: 'openLink',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                                        height: '15px',
                                        width: '15px',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Insert Link',
                                          data: {
                                            name: 'insertLink',
                                            index: 2,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          },
                                        },
                                      },
                                    ],
                                  },
                                  {
                                    type: 'Column',
                                    width: 'auto',
                                    items: [
                                      {
                                        type: 'Image',
                                        url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                                        height: '15px',
                                        width: '15px',
                                        spacing: 'None',
                                        selectAction: {
                                          type: 'Action.Submit',
                                          title: 'Send Link',
                                          data: {
                                            name: 'sendLink',
                                            index: 2,
                                            newImageUrl:
                                              'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                                            link: 'https://kernels-testing.mindtouch.us/Laundry/Washers/Product_Info/Washer_Product_Assistance/Washing_Machine_Comparison_Table',
                                          },
                                        },
                                      },
                                    ],
                                    verticalContentAlignment: 'Center',
                                  },
                                ],
                                horizontalAlignment: 'Right',
                              },
                            ],
                          },
                        ],
                        spacing: 'Medium',
                        separator: true,
                      },
                    ],
                  },
                ],
                spacing: 'Medium',
              },
              {
                type: 'Container',
                id: 'kbImages',
                isVisible: false,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'imagesTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0LjY2NjMgMEgxLjMzMjc3QzAuNTk5MjY3IDAgLTAuMDAwNzMyNDIyIDAuNiAtMC4wMDA3MzI0MjIgMS4zMzM1VjE0LjY2N0MtMC4wMDA3MzI0MjIgMTUuNDAwNSAwLjU5OTI2NyAxNi4wMDA1IDEuMzMyNzcgMTYuMDAwNUgxNC42NjYzQzE1LjM5OTggMTYuMDAwNSAxNS45OTk4IDE1LjQwMDUgMTUuOTk5OCAxNC42NjdWMS4zMzM1QzE1Ljk5OTggMC42IDE1LjM5OTggMCAxNC42NjYzIDBaTTE0LjY2NjMgMS4zMzM1VjEzLjQ2N0wxMS43MzI4IDkuNjY3QzExLjM5OTMgOS4xMzM1IDEwLjk5OTMgOS4wNjcgMTAuNzMyOCA5LjA2N0MxMC4xOTkzIDkuMDY3IDkuNzk5MjcgOS40NjcgOS42NjYyNyA5LjYwMDVMOC45MzI3NyAxMC40MDA1TDUuMzk5MjcgNy4wMDA1QzUuMDY1NzcgNi41MzQgNC43MzI3NyA2LjQ2NyA0LjQ2NTc3IDYuNDY3QzMuOTMyMjcgNi40NjcgMy41MzIyNyA2Ljg2NyAzLjM5OTI3IDcuMDAwNUwxLjMzMjc3IDkuMDY3VjEuMzMzNUgxNC42NjYzWk0xMy45MzMzIDE0LjY2NjVINi41OTk3N0wxMC42NjYzIDEwLjQ2NjVMMTMuOTMzMyAxNC42NjY1Wk0xLjMzMzI3IDExTDQuMzk5NzcgNy44TDcuOTk5NzcgMTEuMzMzNUw0LjczMzI3IDE0LjY2N0gxLjMzMzI3VjExWiIgZmlsbD0iIzNGNUM2OSIvPgo8cGF0aCBkPSJNMTIuMzk5OCA0Ljg2NjVDMTIuMzk5OCA1Ljc4NzUgMTEuNjUzMyA2LjUzMyAxMC43MzMzIDYuNTMzQzkuODEzMjcgNi41MzMgOS4wNjY3NyA1Ljc4NjUgOS4wNjY3NyA0Ljg2NjVDOS4wNjY3NyAzLjk0NTUgOS44MTMyNyAzLjIgMTAuNzMzMyAzLjJDMTEuNjUzMyAzLjIgMTIuMzk5OCAzLjk0NjUgMTIuMzk5OCA0Ljg2NjVaIiBmaWxsPSIjM0Y1QzY5Ii8+Cjwvc3ZnPgo=',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Images (0)',
                            wrap: true,
                            weight: 'Bolder',
                            spacing: 'None',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openImagesContainer',
                      },
                    },
                    separator: true,
                  },
                  {
                    type: 'Container',
                    id: 'imagesContainer',
                    isVisible: false,
                    items: [],
                  },
                ],
                spacing: 'Medium',
              },
              {
                type: 'Container',
                id: 'kbProcessSteps',
                isVisible: false,
                backgroundImage: {
                  url: 'https://www.colorhexa.com/f8f8f9.png',
                  verticalAlignment: 'Top',
                },
                height: '40px',
                width: '100%',
                items: [
                  {
                    type: 'ColumnSet',
                    id: 'processStepsTitle',
                    columns: [
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,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',
                            spacing: 'None',
                            horizontalAlignment: 'Center',
                            selectAction: {
                              type: 'Action.Submit',
                            },
                          },
                        ],
                        verticalContentAlignment: 'Center',
                      },
                      {
                        type: 'Column',
                        width: 'auto',
                        items: [
                          {
                            type: 'TextBlock',
                            text: 'Process Steps (0)',
                            wrap: true,
                            weight: 'Bolder',
                            spacing: 'None',
                            horizontalAlignment: 'Left',
                            isSubtle: false,
                            size: 'small',
                          },
                        ],
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0yLjQ0MTA4IDE1LjVDMS45ODI3NSAxNS41IDEuNTkwMjUgMTUuMzM2NyAxLjI2MzU4IDE1LjAxQzAuOTM2OTE1IDE0LjY4MzMgMC43NzM4NiAxNC4yOTExIDAuNzc0NDE1IDEzLjgzMzNWMi4xNjY2N0MwLjc3NDQxNSAxLjcwODMzIDAuOTM3NzQ5IDEuMzE1ODMgMS4yNjQ0MiAwLjk4OTE2OEMxLjU5MTA4IDAuNjYyNTAxIDEuOTgzMyAwLjQ5OTQ0NiAyLjQ0MTA4IDAuNTAwMDAxSDkuOTQxMDhWMi4xNjY2N0gyLjQ0MTA4VjEzLjgzMzNIMTQuMTA3N1Y2LjMzMzMzSDE1Ljc3NDRWMTMuODMzM0MxNS43NzQ0IDE0LjI5MTcgMTUuNjExMSAxNC42ODQyIDE1LjI4NDQgMTUuMDEwOEMxNC45NTc3IDE1LjMzNzUgMTQuNTY1NSAxNS41MDA2IDE0LjEwNzcgMTUuNUgyLjQ0MTA4Wk00Ljk0MTA4IDEyLjE2NjdWMTAuNUgxMS42MDc3VjEyLjE2NjdINC45NDEwOFpNNC45NDEwOCA5LjY2NjY3VjhIMTEuNjA3N1Y5LjY2NjY3SDQuOTQxMDhaTTQuOTQxMDggNy4xNjY2N1Y1LjVIMTEuNjA3N1Y3LjE2NjY3SDQuOTQxMDhaTTEyLjQ0MTEgNS41VjMuODMzMzNIMTAuNzc0NFYyLjE2NjY3SDEyLjQ0MTFWMC41MDAwMDFIMTQuMTA3N1YyLjE2NjY3SDE1Ljc3NDRWMy44MzMzM0gxNC4xMDc3VjUuNUgxMi40NDExWiIgZmlsbD0iIzNGNUM2OSIvPg0KPC9zdmc+DQo=',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Insert Steps',
                              data: {
                                name: 'insertSteps',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'stretch',
                      },
                      {
                        type: 'Column',
                        items: [
                          {
                            type: 'Image',
                            url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxOSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMTguOTYzNyAxLjI0Mzg1QzE5LjA4MDkgMC44MzI3NzQgMTguODk5OSAwLjQ0OTI5NCAxOC42NDI4IDAuMjMwNDc3QzE4LjM4NzQgMC4wMTMxMzk3IDE3Ljk2ODQgLTAuMTA4NDM2IDE3LjU4NTYgMC4xMzE2NjlMMC44NDE0MjkgOC45MzQ1NEMwLjQwMjQ0NiA5LjExNDM2IDAuMjI4NTQyIDkuNTMzMDkgMC4yODQ2MDEgOS45MTE4NEMwLjMzOTIxMyAxMC4yODA4IDAuNjEwNjczIDEwLjYzMjUgMS4wNDAwMiAxMC43Mzk5TDEuMDYwNzQgMTAuNzQ1MUw4Ljg0MjM2IDExLjkxNjRMMTEuMzUzNyAxOS4zNDEzTDExLjM1NjIgMTkuMzQ4QzExLjUxNTIgMTkuNzcyNSAxMS44OTkxIDIwIDEyLjI3OTMgMjBDMTIuNjYxNCAyMCAxMy4wNDggMTkuNzY1OCAxMy4xNjggMTkuMzE1N0wxOC45NjE3IDEuMjUwODFMMTguOTYzNyAxLjI0Mzg1Wk05LjE3ODI3IDEwLjAxMTRMNC4yNTg1NiA5LjI4MTZMMTQuMTg5MiA0LjA4MzEzTDkuMTc4MjcgMTAuMDExNFpNMTIuMjQ1OSAxNi4wMDg3TDEwLjY0NiAxMS4yNDk1TDE1LjY3NDYgNS4zMDAzN0wxMi4yNDU5IDE2LjAwODdaIiBmaWxsPSIjM0Y1QzY5Ii8+DQo8L3N2Zz4NCg==',
                            height: '15px',
                            width: '15px',
                            horizontalAlignment: 'Right',
                            selectAction: {
                              type: 'Action.Submit',
                              title: 'Send Steps',
                              data: {
                                name: 'sendSteps',
                                newImageUrl:
                                  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxOSAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE3Ljk1NTggMC4zMTU3ODlDMTcuNTMyMyAtMC4xMDUyNjMgMTYuODk3IC0wLjEwNTI2MyAxNi40NzM0IDAuMzE1Nzg5TDcuMTU1NzkgOS41Nzg5NUwyLjA3MzQ0IDQuNzM2ODRDMS42NDk5MSA0LjMxNTc5IDEuMDE0NjEgNC4zMTU3OSAwLjU5MTA4NSA0LjczNjg0QzAuMTY3NTU1IDUuMTU3ODkgMC4xNjc1NTUgNS43ODk0NyAwLjU5MTA4NSA2LjIxMDUzTDYuNTIwNSAxMS42ODQyQzYuNzMyMjYgMTEuODk0NyA2Ljk0NDAzIDEyIDcuMjYxNjcgMTJDNy41NzkzMiAxMiA3Ljc5MTA4IDExLjg5NDcgOC4wMDI4NSAxMS42ODQyTDE3Ljk1NTggMS43ODk0N0MxOC4zNzkzIDEuMzY4NDIgMTguMzc5MyAwLjczNjg0MiAxNy45NTU4IDAuMzE1Nzg5WiIgZmlsbD0iIzI3OEEzNSIvPgo8L3N2Zz4K',
                              },
                            },
                          },
                        ],
                        width: 'auto',
                      },
                    ],
                    selectAction: {
                      type: 'Action.Submit',
                      data: {
                        name: 'openProcessStepsContainer',
                      },
                    },
                    separator: true,
                  },
                ],
                spacing: 'Medium',
              },
            ],
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.6',
          },
        },
        {
          contentType: AgentCopilotContent.CONVERSATION_ASSISTANCE,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_rtSummary',
          content: {
            type: 'AdaptiveCard',
            body: [
              {
                type: 'Container',
                id: 'conversationAssistance',
                isVisible: true,
                items: [
                  {
                    type: 'Container',
                    items: [
                      {
                        type: 'ColumnSet',
                        isVisible: true,
                        columns: [
                          {
                            type: 'Column',
                            items: [
                              {
                                type: 'Input.Toggle',
                                id: 'toggle1',
                                title: '${customerConcern}',
                                value: '${isConcernResolvedByAgent}',
                                when: "${(customerConcern != null) && (customerConcern != '')}",
                              },
                            ],
                            width: 'stretch',
                          },
                          {
                            type: 'Column',
                            items: [
                              {
                                type: 'Image',
                                url: 'AdaptiveCardSearchIcon',
                                height: '15px',
                                width: '15px',
                                horizontalAlignment: 'Right',
                                selectAction: {
                                  type: 'Action.Submit',
                                  title: 'Search Topic',
                                  data: {
                                    name: 'searchTopic',
                                    newImageUrl: 'AdaptiveCardSentIcon',
                                    index: '${$index}',
                                  },
                                },
                              },
                            ],
                            width: 'auto',
                          },
                          {
                            type: 'Column',
                            items: [
                              {
                                type: 'Image',
                                url: 'AdaptiveCardRemoveIcon',
                                height: '15px',
                                width: '15px',
                                horizontalAlignment: 'Right',
                                selectAction: {
                                  type: 'Action.Submit',
                                  title: 'Remove Topic',
                                  data: {
                                    name: 'removeTopic',
                                    newImageUrl: 'AdaptiveCardSentIcon',
                                    index: '${$index}',
                                  },
                                },
                              },
                            ],
                            width: 'auto',
                          },
                        ],
                        $data: '${customerQuestionsConcerns}',
                        index: '${$index}',
                        separator: true,
                      },
                    ],
                  },
                ],
              },
            ],
            $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
            version: '1.6',
          },
        },
        {
          contentType: AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_rtSummary',
          content: {
        
        type: 'AdaptiveCard',
        body: [
            {
                type: 'TextBlock',
                text: '{description}',
                wrap: true,
                weight: 'Bolder',
                size: 'Medium'
            },
            {
                type: 'FactSet',
                facts: []
            }
        ],
        actions: [
            {
                type: 'Action.Submit',
                title: 'Generate Response',
                data: {
                    action: 'generateResponse'
                }
            }
        ],
        $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
        version: '1.6',
              },

        },
        {
          contentType: AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED_LIVE,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_rtSummary',
          content: {
        
        type: 'AdaptiveCard',
        body: [
            {
                type: 'TextBlock',
                text: '{description}',
                wrap: true,
                weight: 'Bolder',
                size: 'Medium'
            },
            {
                type: 'FactSet',
                facts: []
            }
        ],
        actions: [
            {
                type: 'Action.Submit',
                title: 'Generate Response',
                data: {
                    action: 'generateResponse'
                }
            }
        ],
        $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
        version: '1.6',
              },

        },
        {
          contentType: AgentCopilotContent.EMAIL_RESPONSE_GENERATED,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_rtSummary',
          content: {
        
            type: 'AdaptiveCard',
            body: [
                {
                    type: 'TextBlock',
                    text: '{description}',
                    wrap: true,
                    weight: 'Bolder',
                    size: 'Medium'
                }
            ],
          actions: [
            {
                type: 'Action.Submit',
                title: 'Generate Response',
                data: {
                    action: 'generateResponse'
                }
            }
        ],
        $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
        version: '1.6',
              },

        },
        {
          contentType: AgentCopilotContent.EMAIL_RESPONSE_GENERATED_LIVE,
          cardType: AgentCopilotContent.ADAPTIVE_CARD,
          objectId: '238a5956-2cf1-4863-bcd3-8ffe8466b760_rtSummary',
          content: {
        
            type: 'AdaptiveCard',
            body: [
                {
                    type: 'TextBlock',
                    text: '{description}',
                    wrap: true,
                    weight: 'Bolder',
                    size: 'Medium'
                }
            ],
          actions: [
            {
                type: 'Action.Submit',
                title: 'Generate Response',
                data: {
                    action: 'generateResponse'
                }
            }
        ],
        $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
        version: '1.6',
              },

        },
      ],
      responseSent:
        'Hello! How can I assist you today? Please provide more details about your inquiry so that I can better assist you.',
      isResponseInserted: false,
      isResponseSent: false,
      isJourneySummaryExpanded: false,
    },
  },
  isSent: { clicked: false, message: '' },
  isInsert: { clicked: false, message: '' },
  finalSummaryNotes: {summary:'', resolved:false,intents:'',actions:'',outcomes:'', entities:''},
  copilotConfig: {
    generativeResponseTone: 'Formal',
    expertCopilotURL: 'https://kernels-testing.mindtouch.us',
    expertCopilotClientKey:
      '305909958dcd207135dc42ad44c94fb5e7fc2270a197063c35b822f340ba0736',
    expertCopilotClientSecret:
      '1870ca582ce2c86918e5e61edf189999b243f49e1189612a2674f9867415f082',
    botPersona: '',
    userPersona: '',
    path: 'custom',
    associatedFile: '',
    kBAnswersPath: '',
    salesOffersPath: '',
    compliancePath: '',
    enableSentiment: true,
    sentimentResponseLength: 20,
    realTimeSummary: true,
    realTimeSummaryResponseLength: 25,
    finalSummary: true,
    finalSummaryResponseLength: 65,
    behavioralGuidance: true,
    behavioralGuidanceResponseLength: 10,
    behevioralGuidanceNumberOfResponse: 3,
    enableExpertCopilotIntegration: true,
    nextBestResponse: false,
    nextBestResponseLength: 20,
    nextBestNumberOfResponse: 2,
    knowledgeBaseArticles: true,
    knowledgeBaseArticlesResponseLength: 65,
    knowledgeBaseWebLinks: true,
    knowledgeBaseMaximumWebLinks: 3,
    knowledgeBaseImages: true,
    knowledgeBaseMaximumImages: 3,
    knowledgeBaseProcessSteps: true,
    knowledgeBaseMaximumSteps: 10,
    salesOffers: true,
    compliance: true,
    threshold: 0.75,
    threshold_sales: 0.75,
    threshold_compliance: 0.75,
    language: 'en-US',
    backAndForthLiveConvo: false,
		backAndForthLiveConvoLength: 4,
    demoType: '',
  },
  isLiveCopilotEnabled: false,
  isRequestProcessing: false,
  agentSearchTopic: '',
};

export const liveCopilotSlice = createSlice({
  name: CCF_LIVE_COPILOT_KEY,
  initialState: initialCcfLiveCopilotState,
  reducers: {
    /**
     * Function to add card data to existing state
     * @param state - CcfCopilotData
     * @example -
     * ```
     * dispatch(addAdaptiveCardData(payload));
     * ```
     * @returns - this returns state
     */
    addAdaptiveCardData(state, action: PayloadAction<CcfCopilotData>) {
      const caseId = action.payload && Object.keys(action.payload)[0];
      const payloadData = action.payload && Object.values(action.payload)[0];
      const adaptiveCardToAdd = payloadData.acpAppElements;
      const adaptiveCardContentType = adaptiveCardToAdd[0]?.contentType;

      let existingAdaptiveCards =
      state.adaptiveCardsData[caseId] && Array.isArray(state.adaptiveCardsData[caseId].acpAppElements)
        ? [...state.adaptiveCardsData[caseId].acpAppElements]
        : [];

      // if (adaptiveCardContentType === AgentCopilotContent.SENTIMENT) {
      //   const sentimentAdaptiveCards = existingAdaptiveCards.filter(
      //     (card: CopilotElement) =>
      //       card.contentType === AgentCopilotContent.SENTIMENT
      //   );
      //   const lastSentiment =
      //     sentimentAdaptiveCards[sentimentAdaptiveCards.length - 1];

      //   if (
      //     lastSentiment &&
      //     extractCustomerSentiment(JSON.stringify(lastSentiment)) ===
      //       extractCustomerSentiment(JSON.stringify(adaptiveCardToAdd[0]))
      //   ) {
      //     return state;
      //   }
      // }
      if (
        existingAdaptiveCards &&
        adaptiveCardContentType === AgentCopilotContent.RT_SUMMARY
      ) {
        const realtTimeSummaryIndex = existingAdaptiveCards.findIndex(
          (card: CopilotElement) =>
            card.contentType === AgentCopilotContent.RT_SUMMARY
        );
        realtTimeSummaryIndex > -1 &&
          existingAdaptiveCards.splice(realtTimeSummaryIndex, 1);
      }
      if (
        existingAdaptiveCards &&
        adaptiveCardContentType === AgentCopilotContent.CONVERSATION_ASSISTANCE
      ) {
        const filteredAdaptiveCards = existingAdaptiveCards.filter(
          (card: CopilotElement) =>
            card.contentType !== AgentCopilotContent.CONVERSATION_ASSISTANCE
        );
        existingAdaptiveCards = [...filteredAdaptiveCards];
      }

      if (
        existingAdaptiveCards &&
        adaptiveCardContentType === AgentCopilotContent.JOURNEY_SUMMARY
      ) {
        const filteredAdaptiveCards = existingAdaptiveCards.filter(
          (card: CopilotElement) =>
            card.objectId ===
            'e15d4e75-39fa-4f9d-85a1-daffe6d16a40_journeySummary'
        );
        if (filteredAdaptiveCards.length >= 1) return;
      }

      if (adaptiveCardContentType !== AgentCopilotContent.CONVERSATION_ASSISTANCE) {
        // *** Check for duplicate cards based on objectId *** TEMP!!!! TO DO - FIX THE BROADCAST ISSUE
        const newCardObjectId = adaptiveCardToAdd[0]?.objectId;
        const isDuplicateCard = existingAdaptiveCards.some(
          (card: CopilotElement) => card.objectId === newCardObjectId
        );
  
        if (isDuplicateCard) {
          return state;
        }
      }

      const updatedCopilotContactDetails = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            contactId: payloadData.contactId,
            agentAssistSource: payloadData.agentAssistSource,
            utcTimestamp: payloadData.utcTimestamp,
            acpAppElements: [...existingAdaptiveCards, ...adaptiveCardToAdd],
          },
        },
      };
      return updatedCopilotContactDetails;
    },


    /**
     * Function to update card data to existing state
     * @param state - CcfCopilotData
     * @example -
     * ```
     * dispatch(updateAdaptiveCardData(payload));
     * ```
     * @returns - this returns state
     */
    updateAdaptiveCardData(state, action: PayloadAction<CcfCopilotData>) {
      const caseId = action.payload && Object.keys(action.payload)[0];
      const payloadData = action.payload && Object.values(action.payload)[0];
      const adaptiveCardToAdd = payloadData.acpAppElements[0];
      const newAdaptiveCardObjectId = adaptiveCardToAdd?.objectId.includes("_emailResponseGenerated") ? adaptiveCardToAdd?.objectId.replace("_emailResponseGenerated", "_emailResponseSuggested") : adaptiveCardToAdd?.objectId;

      const existingAdaptiveCards =
        state.adaptiveCardsData[caseId]?.acpAppElements || [];

      const existingCardIndex = existingAdaptiveCards.findIndex(
        (card) => card.objectId === newAdaptiveCardObjectId
      );

      let updatedAdaptiveCards;
      if (existingCardIndex !== -1) {
        updatedAdaptiveCards = existingAdaptiveCards.map((card, index) =>
          index === existingCardIndex
            ? { ...card, content: adaptiveCardToAdd.content }
            : card
        );
      } else {
        updatedAdaptiveCards = existingAdaptiveCards;
      }

      const updatedCopilotContactDetails = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            contactId: payloadData.contactId,
            agentAssistSource: payloadData.agentAssistSource,
            utcTimestamp: payloadData.utcTimestamp,
            acpAppElements: updatedAdaptiveCards,
          },
        },
      };

      return updatedCopilotContactDetails;
    },

     /**
     * Function to set blank card data for selected contact to existing state
     * @param state - CcfCopilotData
     * @example -
     * ```
     * dispatch(setBlankAdaptiveCardDataForContact(payload));
     * ```
     * @returns - this returns state
     */
     setBlankAdaptiveCardDataForContact(state, action: PayloadAction<any>) {
      const caseId = action.payload;
     
      const updatedAdaptiveCardData = {...state.adaptiveCardsData};
      delete updatedAdaptiveCardData[caseId];

      const updatedCopilotContactDetails = {
        ...state,
        adaptiveCardsData: updatedAdaptiveCardData,
      };

      return updatedCopilotContactDetails;
    },


    /**
     * Function to set agent copilot next best responses
     * @param state - CcfCopilotData
     * @param action - PayloadAction<CcfCopilotData>
     * @example
     * ```
     * dispatch(setNextBestResponse())
     * ```
     * @returns
     */
    setNextBestResponse(state, action: PayloadAction<CcfCopilotData>) {
      const caseId = Object.keys(action.payload)[0];
      const payloadData = Object.values(action.payload)[0];
      const responsesToAdd = payloadData.acpAppElements[0];
      const existingAssistElements =
        state.adaptiveCardsData[caseId] === undefined
          ? []
          : (state as unknown as CcfLiveCopilotState).adaptiveCardsData[caseId]
              ?.acpAppElements;
      const updatedItems = existingAssistElements?.filter(
        (item: CopilotElement) =>
          !item?.contentType.includes(AgentCopilotContent?.NEXT_BEST_RESPONSE)
      );

      const updatedCopilotContactDetails = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            contactId: payloadData.contactId,
            agentAssistSource: payloadData.agentAssistSource,
            utcTimestamp: payloadData.utcTimestamp,
            acpAppElements: [...updatedItems, responsesToAdd],
          },
        },
      };
      return updatedCopilotContactDetails;
    },

        /**
     * Function to remove agent copilot next best responses
     * @param state - CcfCopilotData
     * @param action - PayloadAction<CcfCopilotData>
     * @example
     * ```
     * dispatch(removeNextBestResponse())
     * ```
     * @returns
     */
        removeNextBestResponse(state, action: PayloadAction<{caseId : string}>) {
          const {caseId} = action.payload;
          const existingAssistElements =
            state.adaptiveCardsData[caseId] === undefined
              ? []
              : (state as unknown as CcfLiveCopilotState).adaptiveCardsData[caseId]
                  ?.acpAppElements;
          const updatedItems = existingAssistElements?.filter(
            (item: CopilotElement) =>
              !item?.contentType.includes(AgentCopilotContent?.NEXT_BEST_RESPONSE)
          );
    
          const updatedCopilotContactDetails = {
            ...state,
            adaptiveCardsData: {
              ...state.adaptiveCardsData,
              [caseId]: {
                ...state.adaptiveCardsData[caseId],
                acpAppElements: [...updatedItems],
              },
            },
          };
          return updatedCopilotContactDetails;
        },

    /**
     * Function to set conversation Assistance
     * @param state - CcfCopilotData
     * @param action - PayloadAction<CcfCopilotData>
     * @example
     * ```
     * dispatch(setConversationAssistance())
     * ```
     * @returns
     */
    setConversationAssistance(state, action: PayloadAction<CcfCopilotData>) {
      const caseId = Object.keys(action.payload)[0];
      const payloadData = Object.values(action.payload)[0];
      const responsesToAdd = payloadData.acpAppElements[0];
      const existingAssistElements =
        state.adaptiveCardsData[caseId] === undefined
          ? []
          : (state as unknown as CcfLiveCopilotState).adaptiveCardsData[caseId]
              ?.acpAppElements;
      const updatedItems = existingAssistElements?.filter(
        (item: CopilotElement) =>
          !item?.contentType.includes(
            AgentCopilotContent?.CONVERSATION_ASSISTANCE
          )
      );

      const updatedCopilotContactDetails = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            contactId: payloadData.contactId,
            agentAssistSource: payloadData.agentAssistSource,
            utcTimestamp: payloadData.utcTimestamp,
            acpAppElements: [...updatedItems, responsesToAdd],
          },
        },
      };
      return updatedCopilotContactDetails;
    },

    /**
     * Function to remove conversation item
     * @param state - CcfCopilotData
     * @param action - PayloadAction<string>
     * @example -
     * ```
     * dispatch(removeConversationAssistanceItem())
     * ```
     * @returns
     */
    removeConversationAssistanceItem(
      state,
      action: PayloadAction<{
        caseId: string;
        assistanceId: number;
        topic: string;
      }>
    ) {
      const { caseId, assistanceId } = action.payload;
      const copilotData = state.adaptiveCardsData[caseId];
      let containerVisible = true;

      const updatedItems = copilotData.acpAppElements.map(
        (adItem: CopilotElement) => {
          if (
            adItem.contentType.includes(
              AgentCopilotContent?.CONVERSATION_ASSISTANCE
            )
          ) {
            const bodyItems = (
              adItem.content as CopilotAdaptiveCard
            ).body.slice();
            if (bodyItems.length === 1) {
              bodyItems.splice(0);
            } else {
              bodyItems.splice(assistanceId, 1);
            }

            if (bodyItems.length === 0) {
              containerVisible = false;
            }

            return {
              ...adItem,
              content: {
                ...adItem.content,
                body: bodyItems,
              },
            };
          }
          return adItem;
        }
      );

      const updatedCards = containerVisible
        ? updatedItems
        : updatedItems.filter(
            (item: CopilotElement) =>
              !item.contentType.includes(
                AgentCopilotContent?.CONVERSATION_ASSISTANCE
              )
          );

      return {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            acpAppElements: updatedCards,
          },
        },
      };
    },

    /**
     * Function to check if suggestion is Clicked or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsSentClicked(true,"123"));
     */
    setIsSentClicked(state, action: PayloadAction<any>) {
      return {
        ...state,
        isSent: action.payload,
      };
    },

    /**
     * Function to check if suggestion is Clicked or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsInsertClicked(true,"123"));
     */
    setIsInsertClicked(state, action: PayloadAction<any>) {
      return {
        ...state,
        isInsert: action.payload,
      };
    },

    /**
     * Function to check if suggestion is Clicked or not
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setIsInsertClicked(true,"123"));
     */
    setFinalSummary(state, action: PayloadAction<any>) {
      let formattedSummary = action.payload.summary;
      try {
        const parsedSummary = JSON.parse(action.payload.summary);
        formattedSummary = JSON.stringify(parsedSummary, null, 2);
      } catch (error) {
        console.error('Error parsing summary:', error);
      }
      return {
        ...state,
        finalSummaryNotes: {
          ...action.payload,
          summary: formattedSummary,
        },
      };
    },

    /**
     * Function for setting copilot config
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setCopilotConfig(state, action: PayloadAction<any>) {
      return {
        ...state,
        copilotConfig: action.payload,
      };
    },

    /**
     * Function for setting index and persona config
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setIndexConfig(state, action: PayloadAction<any>) {
      return {
        ...state,
        indexConfig: action.payload,
      };
    },

    /**
     * Function for setting if live copilot is enabled
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setIsLiveCopilotEnabled(state, action: PayloadAction<any>) {
      return {
        ...state,
        isLiveCopilotEnabled: action.payload,
      };
    },

    /**
     * Function for setting agent Search Topic
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setAgentSearchTopic(state, action: PayloadAction<any>) {
      return {
        ...state,
        agentSearchTopic: action.payload,
      };
    },

    /**
     * Function for setting if request is processing
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setIsRequestProcessing(state, action: PayloadAction<any>) {
      return {
        ...state,
        isRequestProcessing: action.payload,
      };
    },

    /**
     * Function for setting bot assist value
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setBotAssistValue1(state, action: PayloadAction<any>) {
      return {
        ...state,
        botAssistValue1: action.payload,
      };
    },

    /**
     * Function for setting bot assist value
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setBotAssistValue2(state, action: PayloadAction<any>) {
      return {
        ...state,
        botAssistValue2: action.payload,
      };
    },

    /**
     * Function for setting bot assist value
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setBotAssistValue3(state, action: PayloadAction<any>) {
      return {
        ...state,
        botAssistValue3: action.payload,
      };
    },

    /**
     * Function for setting bot assist value
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setBotAssistValue4(state, action: PayloadAction<any>) {
      return {
        ...state,
        botAssistValue4: action.payload,
      };
    },

    /**
     * Function for setting bot assist value
     * @param state -
     * @param action -
     * @returns
     * @example -
     */
    setBotAssistValue5(state, action: PayloadAction<any>) {
      return {
        ...state,
        botAssistValue5: action.payload,
      };
    },

    /**
     * updating responseSent state value after agent send text in editor
     * @param state - CcfCopilotData
     * @param action  - `PayloadAction<{ response: string; caseId: string }>`
     * @example -
     * ```
     * dispatch(updateSentBestResponse(@param))
     * ```
     */
    updateSentBestResponse(
      state,
      action: PayloadAction<{ response: string; caseId: string }>
    ) {
      const caseId = action.payload.caseId;
      const updatedResponseSentState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            responseSent: action.payload.response,
          },
        },
      };
      return updatedResponseSentState;
    },

    /**
     * updating isResponseInserted state value after sent out reply
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(updateIsBestResponseSent(@param))
     * ```
     */
    updateIsBestResponseInserted(
      state,
      action: PayloadAction<{ isResponseInserted: boolean; caseId: string }>
    ) {
      const caseId = action?.payload?.caseId;
      const updatedResponseSentState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            isResponseInserted: action?.payload?.isResponseInserted,
          },
        },
      };
      return updatedResponseSentState;
    },

    /**
     * updating isResponseInserted state value after sent out reply
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(updateIsBestResponseSent(@param))
     * ```
     */
    updateIsBestResponseSent(
      state,
      action: PayloadAction<{ isResponseSent: boolean; caseId: string }>
    ) {
      const caseId = action?.payload?.caseId;
      const updatedResponseSentState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            isResponseSent: action?.payload?.isResponseSent,
          },
        },
      };
      return updatedResponseSentState;
    },

    /**
     * updating isCopilotAvailable state value
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(updateAdaptiveCardSchema(@param))
     * ```
     */
    updateAdaptiveCardSchema(
      state,
      action: PayloadAction<{
        updatedCopilotCard: any;
        caseId: string;
        cardId: number;
        containerId: string;
        feedbackIndex?: any;
        feedbackIcons?: boolean;
      }>
    ) {
      const { updatedCopilotCard, caseId, cardId, containerId, feedbackIndex, feedbackIcons } = action.payload;
      const copilotData = state.adaptiveCardsData[caseId];
      const adaptiveCardsData: CopilotElement[] = [];
      const textCardsData: CopilotElement[] = [];

      copilotData.acpAppElements.forEach((item: CopilotElement) => {
        if (
          item.cardType === AgentCopilotContent.ADAPTIVE_CARD &&
          item.contentType !== AgentCopilotContent.CONVERSATION_ASSISTANCE
        )
          adaptiveCardsData.push(item);
        else textCardsData.push(item);
      });
    
      const updatedElements = adaptiveCardsData.map((element: CopilotElement, index: number) => {
        if (index === cardId) {
          let updatedBody;
          if (containerId === ContainerId.FEEDBACK_COMMENTS_CONTAINER_LIKE || containerId === ContainerId.FEEDBACK_COMMENTS_CONTAINER_DISLIKE) {
            updatedBody = [...(element.content as CopilotAdaptiveCard).body];
            if (feedbackIndex >= 0 && feedbackIndex < updatedBody.length) {
              updatedBody[feedbackIndex] = updatedCopilotCard;
            }
            if (feedbackIcons) {
              updatedBody[feedbackIndex] = updatedCopilotCard;
            }
          } 
          else if (
            containerId === ContainerId.REPLY_GENERATING_CONTAINER ||
            containerId === ContainerId.SHOW_TOPIC_CONTAINER
          ) {
            updatedBody = updatedCopilotCard;
          }
          else {
            updatedBody = (element.content as CopilotAdaptiveCard).body.map((container: CardElement) => {
              if (
                (containerId === AgentCopilotContent.JOURNEY_SUMMARY ||
                  containerId === ContainerId.JOURNEY_SUMMARY_CONTAINER ||
                  containerId === ContainerId.WORKFLOW_QUESTIONS_CONTAINER ||
                  containerId === ContainerId.WORKFLOW_BUTTONS_CONTAINER) &&
                container.id === containerId &&
                container.type === AdaptiveCardType.CONTAINER
              ) {
                return updatedCopilotCard;
              }
              if (
                container.id === containerId &&
                container.type === AdaptiveCardType.CONTAINER
              ) {
                return {
                  ...container,
                  items: [...updatedCopilotCard],
                };
              }
              return container;
            });
          }
    
          return {
            ...element,
            content: {
              ...element.content,
              body: updatedBody,
            } as CopilotAdaptiveCard,
          };
        }
        return element;
      });
    
      const updatedState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            acpAppElements: [...textCardsData, ...updatedElements],
          },
        },
      };
    
      return updatedState;
    },    

    /**
     * Function to Set to initial state
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setToInitialState(true,"123"));
     */
    setCopilotToInitialState() {
      return {
        adaptiveCardsData: {
          '0': {
            contactId: '0',
            agentAssistSource: '',
            utcTimestamp: new Date(),
            acpAppElements: [],
            responseSent: '',
            isResponseInserted: false,
            isResponseSent: false,
            isJourneySummaryExpanded: false,
          },
        },
        isSent: { clicked: false, message: '' },
        isInsert: { clicked: false, message: '' },
        finalSummaryNotes: {summary:'', resolved:false,intents:'',actions:'',outcomes:'', entities:''},
        copilotConfig: {
          generativeResponseTone: 'Formal',
          expertCopilotURL: 'https://kernels-testing.mindtouch.us',
          expertCopilotClientKey:
            '305909958dcd207135dc42ad44c94fb5e7fc2270a197063c35b822f340ba0736',
          expertCopilotClientSecret:
            '1870ca582ce2c86918e5e61edf189999b243f49e1189612a2674f9867415f082',
          index: '',
          botPersona: '',
          userPersona: '',
          path: '',
          associatedFile: '',
          kBAnswersPath: '',
          salesOffersPath: '',
          compliancePath: '',
          enableSentiment: true,
          sentimentResponseLength: 20,
          realTimeSummary: true,
          realTimeSummaryResponseLength: 10,
          finalSummary: true,
          finalSummaryResponseLength: 100,
          behavioralGuidance: true,
          behavioralGuidanceResponseLength: 10,
          behevioralGuidanceNumberOfResponse: 3,
          enableExpertCopilotIntegration: true,
          nextBestResponse: true,
          nextBestResponseLength: 20,
          nextBestNumberOfResponse: 2,
          knowledgeBaseArticles: true,
          knowledgeBaseArticlesResponseLength: 40,
          knowledgeBaseWebLinks: true,
          knowledgeBaseMaximumWebLinks: 5,
          knowledgeBaseImages: true,
          knowledgeBaseMaximumImages: 5,
          knowledgeBaseProcessSteps: true,
          knowledgeBaseMaximumSteps: 10,
          salesOffers: true,
          compliance: true,
          threshold: 0.75,
          threshold_sales: 0.75,
          threshold_compliance: 0.75,
          language: 'en-US',
          backAndForthLiveConvo: false,
		      backAndForthLiveConvoLength: 4,
          demoType: '',
        },
        isLiveCopilotEnabled: false,
        isRequestProcessing: false,
        agentSearchTopic: '',
        botAssistValue1: '',
        botAssistValue2: '',
        botAssistValue3: '',
        botAssistValue4: '',
        botAssistValue5: '',
        audioTranscript:''
      };
    },

    /**
     * updating isJourneySummaryExpanded state value
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(updateIsJourneySummaryExpanded(@param))
     * ```
     */
    updateIsJourneySummaryExpanded(
      state,
      action: PayloadAction<{
        isJourneySummaryExpanded: boolean;
        caseId: string;
      }>
    ) {
      const caseId = action?.payload?.caseId;
      const updatedResponseState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            isJourneySummaryExpanded: action?.payload?.isJourneySummaryExpanded,
          },
        },
      };
      return updatedResponseState;
    },

    /**
     * removing adaptive cards from contentType
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(removeAdaptiveCardbyContentType(@param))
     * ```
     */
    removeAdaptiveCardbyContentType(
      state,
      action: PayloadAction<{ caseId: string; contentType: string }>
    ) {
      const { caseId, contentType } = action.payload;
      const copilotData = state.adaptiveCardsData[caseId];
      const adaptiveCardsData: CopilotElement[] = [];
      const textCardsData: CopilotElement[] = [];
      copilotData.acpAppElements.forEach((item: CopilotElement) => {
        if (item.cardType === AgentCopilotContent.ADAPTIVE_CARD)
          adaptiveCardsData.push(item);
        else textCardsData.push(item);
      });

      const updatedAdaptiveCards = adaptiveCardsData.filter(
        (element) =>
          element.cardType === AgentCopilotContent.ADAPTIVE_CARD &&
          element.contentType !== contentType
      );
      const updatedResponseState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            acpAppElements: [...textCardsData, ...updatedAdaptiveCards],
          },
        },
      };
      return updatedResponseState;
    },

        /**
     * set Conversation topics 
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(setConverstionAssistanceTopics(@param))
     * ```
     */
        setConversationAssistanceTopics(
          state,
          action: PayloadAction<{ caseId: string; conversationTopics: any}>
        ) {
          const caseId = action?.payload?.caseId;
          const updatedResponseState = {
            ...state,
            adaptiveCardsData: {
              ...state.adaptiveCardsData,
              [caseId]: {
                ...state.adaptiveCardsData[caseId],
                conversationTopics: action?.payload?.conversationTopics,
              },
            },
          };
          return updatedResponseState;
        },

    /**
     * removing adaptive cards from contentType 
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(removeAdaptiveCardbyContentType(@param))
     * ```
     */
    isFeedbackSaved(state, action: PayloadAction<{ caseId: string; isFeedbackSaved: any}>) {
      const caseId = action?.payload?.caseId;
          const updatedResponseState = {
            ...state,
            adaptiveCardsData: {
              ...state.adaptiveCardsData,
              [caseId]: {
                ...state.adaptiveCardsData[caseId],
                isFeedbackSaved: action?.payload?.isFeedbackSaved,
              },
            },
          };
          return updatedResponseState;
    },

    /**
     * Function to setAudio transcript received from API
     * @param state - copilot state
     * @param action  - PayloadAction<any>
     * @example dispatch(setAudioTranscript(["What is imputed income"]));
     */
       setAudioTranscript(state, action: PayloadAction<any>) {
        return {
          ...state,
          audioTranscript: action.payload,
        };
      },

      /**
     * set isEditorActionPerformed state value 
     * @param state - CcfCopilotData
     * @param action - PayloadAction<boolean>
     * @example -
     * ```
     * dispatch(setIsEditorActionPerformed(@param))
     * ```
     */
    setIsEditorActionPerformed(
      state,
      action: PayloadAction<{ isEditorActionPerformed: boolean; caseId: string }>
    ) {
      const caseId = action?.payload?.caseId;
      const updatedResponseSentState = {
        ...state,
        adaptiveCardsData: {
          ...state.adaptiveCardsData,
          [caseId]: {
            ...state.adaptiveCardsData[caseId],
            isEditorActionPerformed: action?.payload?.isEditorActionPerformed,
          },
        },
      };
      return updatedResponseSentState;
    },
  },
});

export const liveCopilotReducer = liveCopilotSlice.reducer;
export const {
  addAdaptiveCardData,
  updateAdaptiveCardData,
  setNextBestResponse,
  setCopilotToInitialState,
  setIndexConfig,
  setCopilotConfig,
  setFinalSummary,
  setIsSentClicked,
  setIsInsertClicked,
  setIsLiveCopilotEnabled,
  updateAdaptiveCardSchema,
  updateSentBestResponse,
  updateIsBestResponseSent,
  updateIsBestResponseInserted,
  setIsRequestProcessing,
  setConversationAssistance,
  removeConversationAssistanceItem,
  setAgentSearchTopic,
  updateIsJourneySummaryExpanded,
  setBlankAdaptiveCardDataForContact,
  setBotAssistValue1,
  setBotAssistValue2,
  setBotAssistValue3,
  setBotAssistValue4,
  setBotAssistValue5,
  removeAdaptiveCardbyContentType,
  setConversationAssistanceTopics,
  isFeedbackSaved,
  removeNextBestResponse,
  setAudioTranscript,
  setIsEditorActionPerformed, 
} = liveCopilotSlice.actions;
export default liveCopilotSlice.reducer;

/**
 * used to getcopilotState
 * @param rootState - copilot state
 * @example - const copilotState = getcopilotState(state)
 */
const getcopilotState = (rootState: { liveCopilot: CcfLiveCopilotState }) => {
  return rootState[CCF_LIVE_COPILOT_KEY];
};

export const getFinalSummary = createSelector(
  getcopilotState,
  (state) => state.finalSummaryNotes
);

export const getCopilotConfig = createSelector(getcopilotState, (state) => {
  return state.copilotConfig;
});

export const getIsInsert = createSelector(getcopilotState, (state) => {
  return state.isInsert;
});

export const getIsSent = createSelector(getcopilotState, (state) => {
  return state.isSent;
});

export const getIsLiveCopilotEnabled = createSelector(
  getcopilotState,
  (state) => {
    return state.isLiveCopilotEnabled;
  }
);

export const getIsRequestProcessing = createSelector(
  getcopilotState,
  (state) => {
    return state.isRequestProcessing;
  }
);

export const getAgentSearchTopic = createSelector(getcopilotState, (state) => {
  return state.agentSearchTopic;
});

export const getBotAssistValue1 = createSelector(getcopilotState, (state) => {
  return state.botAssistValue1;
});

export const getBotAssistValue2 = createSelector(getcopilotState, (state) => {
  return state.botAssistValue2;
});

export const getBotAssistValue3 = createSelector(getcopilotState, (state) => {
  return state.botAssistValue3;
});

export const getBotAssistValue4 = createSelector(getcopilotState, (state) => {
  return state.botAssistValue4;
});

export const getBotAssistValue5 = createSelector(getcopilotState, (state) => {
  return state.botAssistValue5;
});

export const getLatestAudioTranscript = createSelector(getcopilotState, (state) => {
  return state.audioTranscript ? state.audioTranscript : null;
});

/**
 * Used to get copilot adaptive cards by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getAdaptiveCards('123123');
 * ```
 */
export const getAdaptiveCards = (caseId: string) =>
  createSelector(getcopilotState, (state) => {
    const acpAppElements =
      state.adaptiveCardsData[caseId]?.acpAppElements || {};
    const adaptiveCards = Object.values(acpAppElements).filter(
      (element) => element.cardType === AgentCopilotContent.ADAPTIVE_CARD
    );
    const adaptiveCardsExceptConversationAssistance = Object.values(
      adaptiveCards
    ).filter(
      (element) =>
        element.contentType !== AgentCopilotContent.CONVERSATION_ASSISTANCE
    );
    // const intentElement = adaptiveCardsExceptConversationAssistance.find((element: any) => element.contentType === AgentCopilotContent.INTENT_ASSISTANCE);
    // const filteredArray = adaptiveCardsExceptConversationAssistance.filter((element: any) => element.contentType !== AgentCopilotContent.INTENT_ASSISTANCE);
    // const adaptiveCardsWithIntentAtFirstIndex = intentElement ? [intentElement, ...filteredArray] : filteredArray;
    return adaptiveCardsExceptConversationAssistance;
  });

/**
 * Used to get copilot adaptive cards by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getAdaptiveCards('123123');
 * ```
 */
export const getConversationAssistanceAdaptiveCards = (caseId: string) =>
  createSelector(getcopilotState, (state) => {
    const acpAppElements =
      state.adaptiveCardsData[caseId]?.acpAppElements || {};
    const adaptiveCards = Object.values(acpAppElements).filter(
      (element) => element.cardType === AgentCopilotContent.ADAPTIVE_CARD
    );
    const conversationAssistanceCards = Object.values(adaptiveCards).filter(
      (element) =>
        element.contentType === AgentCopilotContent.CONVERSATION_ASSISTANCE
    );
    return conversationAssistanceCards;
  });

/**
 * Used to get copilot adaptive cards by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getJourneySummaryAdaptiveCard('123123');
 * ```
 */
export const getJourneySummaryAdaptiveCard = (caseId: string) =>
  createSelector(getcopilotState, (state) => {
    const acpAppElements =
      state.adaptiveCardsData[caseId]?.acpAppElements || {};
    const adaptiveCards = Object.values(acpAppElements).filter(
      (element) => element.cardType === AgentCopilotContent.ADAPTIVE_CARD
    );
    const journeySummaryAdaptiveCard = Object.values(adaptiveCards).filter(
      (element) => element.contentType === AgentCopilotContent.JOURNEY_SUMMARY
    );
    return journeySummaryAdaptiveCard;
  });

/**
 * Used to get copilot next best responses by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getNextBestResponses('123123');
 * ```
 */
export const getNextBestResponses = (caseId: string, isACPEnabled = true) =>
  createSelector(getcopilotState, (state) => {
    if (!isACPEnabled) {
      return null;
    }
    const acpAppElements =
      state.adaptiveCardsData[caseId]?.acpAppElements || {};
    const nextBestResponses = Object.values(acpAppElements).filter((element) =>
      element.contentType?.includes(AgentCopilotContent.NEXT_BEST_RESPONSE)
    );
    if (nextBestResponses.length > 0) {
      const content = nextBestResponses[0].content;
      if (content) {
        const { bestResponse } = JSON.parse(`${content}`);
        let trimmedArray = [];
        if (bestResponse.length === 1) {
          const array = bestResponse[0].split(/\d+\./);
          if (array[0] === '') array.shift();
          trimmedArray = array.map((item: string) => item.trim());
        } else if (bestResponse.length > 1) {
          trimmedArray = bestResponse;
        }
        return trimmedArray || [];
        // const data: CopilotNextBestResponse = {
        //   nextBestResponses: {
        //     type: content.nextBestResponses.type,
        //     bestResponse: content.nextBestResponses.bestResponse,
        //   },
        // };
        // return data.nextBestResponses.bestResponse;
      }
    }
    return null;
  });

/**
 * Used to get copilot next best responses by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getNextBestResponses('123123');
 * ```
 */
export const getNextBestResponsesObjectId = (
  caseId: string,
  isACPEnabled = true
) =>
  createSelector(getcopilotState, (state) => {
    if (!isACPEnabled) {
      return null;
    }
    const acpAppElements =
      state.adaptiveCardsData[caseId]?.acpAppElements || {};
    const nextBestResponses = Object.values(acpAppElements).filter((element) =>
      element.contentType?.includes(AgentCopilotContent.NEXT_BEST_RESPONSE)
    );

    return nextBestResponses[0]?.objectId;
  });

  /**
 * Used to get copilot kbcombo by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getKbComboSchema('123123');
 * ```
 */
  export const getKbComboSchema = (caseId: string, isACPEnabled = true) =>
    createSelector(getcopilotState, (state) => {
      if (!isACPEnabled) {
        return null;
      }
      const acpAppElements =
        state.adaptiveCardsData[caseId]?.acpAppElements || {};
      const kbCombo = Object.values(acpAppElements).filter((element) =>
        element.contentType?.includes(AgentCopilotContent.KB_COMBO || element.contentType?.includes(AgentCopilotContent.KB_COMBO_VOICE))
      );
    
      return kbCombo[kbCombo.length - 1];
    });

/**
 * Used to get responseSent by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getSentBestResponse('123123');
 * ```
 */
export const getSentBestResponse = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => state.adaptiveCardsData[caseId]?.responseSent
  );

/**
 * Used to get isResponseInserted by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getIsResponseInserted('123123');
 * ```
 */
export const getIsResponseInserted = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => state.adaptiveCardsData[caseId]?.isResponseInserted
  );

/**
 * Used to get isResponseSent by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getIsResponseSent('123123');
 * ```
 */
export const getIsResponseSent = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => state.adaptiveCardsData[caseId]?.isResponseSent
  );

/**
 * Used to get isJourneySummary by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getIsJourneySummaryExpanded('123123');
 * ```
 */
export const getIsJourneySummaryExpanded = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => state.adaptiveCardsData[caseId]?.isJourneySummaryExpanded
  );
/**
 * Used to get copilot running summary by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * selectRunningSummary('123123');
 * ```
 */
export const selectRunningSummary = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => {
      const acpAppElements = state.adaptiveCardsData[caseId]?.acpAppElements;
      if (Array.isArray(acpAppElements)) {
        return acpAppElements
          .filter((card) => card.contentType === AgentCopilotContent.RT_SUMMARY)
          .pop() || null;
      }
      return null;
    }
  );

/**
 * Used to get conversation assistance by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getConversationAssistance('123123');
 * ```
 */
export const getConversationAssistance = (caseId: string) =>
  createSelector(getcopilotState, (state) => {
    const elements =
      state.adaptiveCardsData[caseId]?.acpAppElements
        .filter(
          (card) =>
            card.contentType === AgentCopilotContent.CONVERSATION_ASSISTANCE
        )
        .pop() || null;
    const content = elements?.content ? JSON.parse(elements?.content) : null;
    const concernsArray = content?.customerQuestionsConcerns ?? [];
    const filteredArray = concernsArray.filter(
      (item: any) =>
        item.customerConcern !== '' &&
        item.customerConcern !== null &&
        item.customerConcern !== 'None'
    );
    return filteredArray ?? [];
  });

/**
 * Used to get conversationTopics by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getIsJourneySummaryExpanded('123123');
 * ```
 */
export const getLiveConversationTopics = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => state.adaptiveCardsData[caseId]?.conversationTopics
  );

  /**
 * Used to get conversationTopics by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getIsJourneySummaryExpanded('123123');
 * ```
 */
export const getisFeedbackSaved = (caseId: string) =>
  createSelector(
    getcopilotState,
    (state) => state.adaptiveCardsData[caseId]?.isFeedbackSaved
  );


/**
 * Used to get copilot email generation request status by caseId
 * @param caseId - Case Id
 * @example -
 * ```
 * getCopilotEmailRequestStatus('123123');
 * ```
 */
export const getCopilotEmailRequestStatus = (caseId: string) =>
  createSelector(getcopilotState, (state) => {
    const acpAppElements = state.adaptiveCardsData[caseId]?.acpAppElements || {};
    const currentEmailCreationCards = Object.values(acpAppElements)?.filter((element) =>
      element?.contentType?.includes(AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED || AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED_LIVE)
    );

    if (currentEmailCreationCards.length === 0) {
      return;
    }
    const currentEmailCreationCard = currentEmailCreationCards[0].content as CopilotAdaptiveCard;
    const replyGeneratingContainer = currentEmailCreationCard?.body?.find((item: { id: ContainerId; }) => item.id === ContainerId.REPLY_GENERATING_CONTAINER);

    return replyGeneratingContainer?.isVisible
   
  });

/**
 * Used to get isEditorActionPerformed by caseId
 * @param caseId - Case Id
 * @example - 
 * ```
 * getIsEditorActionPerformed('123123');
 * ```
 */
export const getIsEditorActionPerformed = (caseId: string) =>
  createSelector(getcopilotState, (state) => (state)?.adaptiveCardsData[caseId]?.isEditorActionPerformed);


/**
 * Function extract customer sentiment
 * @example -
 * ```
 * extractCustomerSentiment(inputStr)
 * ```
 * @returns - string
 */
const extractCustomerSentiment = (inputStr: string) => {
  const sentimentMatch = /Customer Sentiment is (\w+)/.exec(inputStr);
  if (sentimentMatch && sentimentMatch[1]) {
    return sentimentMatch[1];
  }
  return '';
};

/**
 * Function extract sentiment url icon
 * @param content - adaptive card content
 * @example -
 * ```
 * extractSentimentIcon(content)
 * ```
 * @returns - string
 */
export const extractSentimentIcon = (content: string) => {
  const sentimentMatch = /"url":"(\w+)"/g.exec(content);
  if (sentimentMatch && sentimentMatch[1]) {
    return sentimentMatch[1];
  }
  return '';
};

/**
 * Function to replace URLs in the adaptive card data
 * @param adaptiveCardToAdd - CcfCopilotData
 * @param url - URL
 * @param newUrl - Replacement data
 * @returns Updated CcfCopilotData
 * @example -
 * ```
 * replaceUrls(adaptiveCardToAdd,url, replacement)
 * ```
 */
const replaceUrls = (
  adaptiveCardToAdd: CcfCopilotData,
  url: string,
  newUrl: string
): CcfCopilotData => {
  const updatedData = JSON.stringify(adaptiveCardToAdd);
  const updatedAdaptiveCardToAdd = updatedData.replace(
    new RegExp(url, 'g'),
    newUrl
  );
  return JSON.parse(updatedAdaptiveCardToAdd);
};

/**
 * Function to update visibility params of containers in adaptive cards schema
 * @param adapativeCardDetails - adaptive card data
 * @param comboCardDetails - combo card details
 * @example -
 * ```
 * updateContainerVisibility(adapativeCardDetails, comboCardDetails)
 * ```
 * @returns - adaptive card data
 */
export const updateContainerVisibility = (
  adapativeCardDetails: CardData,
  comboCardDetails: ComboCard
): CardData => {
  //check here why processsteps (0) is still set as visible
  adapativeCardDetails.body.forEach((container: CardElement) => {
    const containerId = container.id || '';
    const content = comboCardDetails[containerId] as Record<string, string>[];

    if (content?.length > 0) {
      container.isVisible = true;
    }

    if (containerId === ContainerId.KB_LINKS && content?.length === 0) {
      container.isVisible = false;
    }
    if (containerId === ContainerId.KB_PROCESS_STEPS && content?.length < 2) {
      container.isVisible = false;
    }
    if (containerId === ContainerId.KB_IMAGES && content?.length === 0) {
      container.isVisible = false;
    }
  });
  return adapativeCardDetails;
};


/**
 * Function to add adaptive card data having svgs
 * @param icons - SVG icons array
 * @param adaptiveCardToAdd - CcfCopilotData
 * @param urls - URL array
 * @param store - Redux store
 * @param iconsList - array of icons
 * @example -
 * ```
 * addAdaptiveCard(icons,adaptiveCardToAdd, urls, store, iconList)
 * ```
 */
export const addAdaptiveCard = async (
  contactId: string,
  adaptiveCardToAdd: CcfCopilotData,
  urls: string[],
  dispatch: any,
  iconsList: copilotAdaptiveCardIcon,
  shouldUpdateAdaptiveCard?: boolean,
  showGreenConfidenceDot?: boolean,
) => {
  try {
    const updatedAdaptiveCardToAdd = { ...adaptiveCardToAdd };
    const card = updatedAdaptiveCardToAdd[contactId].acpAppElements[0];

    const adaptiveCardTemplates: any = allAdaptiveCardSchemas;
    let adaptiveCardSchema = adaptiveCardTemplates[card.contentType];
    if (!adaptiveCardSchema) {
      return;
    }
    // let cardContent = card.content;
    let cardContent =
    typeof card.content != 'object'
      ? JSON.parse(card.content as unknown as string)
      : card.content;

    if (
      card.contentType !== AgentCopilotContent.JOURNEY_SUMMARY &&
      card.contentType !== AgentCopilotContent.INTENT_COMPLETED &&
      card.contentType !== AgentCopilotContent.AUTOPILOT_TRANSFER &&
      card.contentType !== AgentCopilotContent.AGENT_TRANSFER &&
      card.contentType !== AgentCopilotContent.FEEDBACK 
    ) {
      cardContent = JSON.parse(card.content as unknown as string);
    }


    if (
      (card.contentType === AgentCopilotContent.KB_COMBO_VOICE ||
       card.contentType === AgentCopilotContent.KB_COMBO) &&
      adaptiveCardToAdd
    ) {
      if(cardContent.kbAnswers.description === 'Sorry, I am unable to assist you with this request.'){
        return;
      }
      const cardDetails = cardContent as unknown as ComboCard;
      adaptiveCardSchema = updateContainerVisibility(
        adaptiveCardSchema,
        cardDetails
      );
      
    }

    const template = new ACData.Template(adaptiveCardSchema);
    let contextForAdaptiveCard = {
      $root: { ...cardContent },
    };
    // if (card.contentType === AgentCopilotContent.EMAIL_RESPONSE) {
    //   console.log('EMAIL_RESPONSE', cardContent);
    //   const emailResponseContent = { cardContent?.description}
    //   const emailTopicsContent = cardContent?.emailTopics;
    //   contextForAdaptiveCard = {
    //     $root: {description},
    //   };
    // }

    if((card.contentType === AgentCopilotContent.KB_COMBO || card.contentType === AgentCopilotContent.KB_COMBO_VOICE) && adaptiveCardToAdd){
      const kbContent = {...cardContent, kbAnswers: {...cardContent.kbAnswers, showGreenConfidenceDot} }
      contextForAdaptiveCard = {
             $root: kbContent,
           }
     }

    if (
      (card.contentType === AgentCopilotContent.SALES_OFFER ||
        card.contentType === AgentCopilotContent.SALES_OFFER_VOICE) &&
      adaptiveCardToAdd
    ) {
      const ordersLength = cardContent?.offers?.offers?.length;
      /* const salesOfferContent = {
        ...cardContent?.offers?.offers[ordersLength - 1],
      } ?? { ...cardContent?.offers }; */
      const salesOfferContent = cardContent?.offers?.offers[ordersLength - 1]
      ? { ...cardContent.offers.offers[ordersLength - 1] }
      : { ...cardContent?.offers };
      contextForAdaptiveCard = {
        $root: salesOfferContent,
      };
    }

    if (
      card.contentType === AgentCopilotContent.CONVERSATION_ASSISTANCE &&
      adaptiveCardToAdd
    ) {
      const data = { ...cardContent };
      const topics = cardContent?.customerQuestionsConcerns;
      const uniqueTopics = topics.filter((topic: any, index: number) => {
        const firstIndex = topics.findIndex(
          (t: any) => t.customerConcern === topic.customerConcern
        );
        return firstIndex === index;
      });

      const filteredUniqueTopics = uniqueTopics.filter((topic: any) => topic.customerConcern.trim() !== '');
      dispatch(setConversationAssistanceTopics({caseId: contactId, conversationTopics : filteredUniqueTopics}));
      contextForAdaptiveCard = {
        $root: { ...data, customerQuestionsConcerns: uniqueTopics },
      };
    }

    let copilotCard = template.expand(contextForAdaptiveCard);
    for (let i = 0; i < urls.length; i++) {
      const svgUrl = urls[i];
      copilotCard = replaceUrls(copilotCard, svgUrl, iconsList[svgUrl]);
    }

    if (card.contentType === AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED || card.contentType === AgentCopilotContent.EMAIL_RESPONSE_SUGGESTED_LIVE) {
      copilotCard.body[0].items[0].columns[1].items[0].text = cardContent?.subtitle;
      copilotCard.body[0].items[2].items[0].text = `adp_responseTopics (${cardContent.emailTopics.length})`;
      const choiceSet = copilotCard.body[0].items[2].items[1];
      choiceSet.choices = [];
      cardContent.emailTopics.forEach((topic: any) => {
        choiceSet.choices.push({
          title: topic.content,
          value: topic.topicId
        });
      });
      // Set selected topic in the choice set
      choiceSet.value = cardContent?.selectedTopicId;
      copilotCard.body[0].items[3].items[0].actions[0].data.emailTopics = cardContent.emailTopics;
      copilotCard.body[0].items[3].items[0].actions[0].data.selectedTopicId = cardContent.selectedTopicId;
      copilotCard.body[1].items[0].columns[1].items[0].text = cardContent?.generationTitle;
      copilotCard.body[1].items[1].text = cardContent?.generationDescription;
    }
    
    if (card.contentType === AgentCopilotContent.EMAIL_RESPONSE_GENERATED || card.contentType === AgentCopilotContent.EMAIL_RESPONSE_GENERATED_LIVE) {
      copilotCard.body[0].title = cardContent?.title;
      copilotCard.body[0].items[1].text = cardContent?.description;
    }

    if (
      card.contentType === AgentCopilotContent.CONVERSATION_ASSISTANCE &&
      copilotCard?.body.length === 0
    ) {
      return;
    }
    updatedAdaptiveCardToAdd[contactId].acpAppElements[0].content = copilotCard;
    // if(card.contentType === AgentCopilotContent.CONVERSATION_ASSISTANCE){
    //   dispatch(setConversationAssistance(updatedAdaptiveCardToAdd));
    // } else{
      // adaptiveCardSchema = updateProcessSteps(
      //   adaptiveCardSchema,
      //   prevKbComboSchema,
      //   updatedAdaptiveCardToAdd
      // );
    //}

    if (shouldUpdateAdaptiveCard) {
      dispatch(updateAdaptiveCardData(updatedAdaptiveCardToAdd));
    }
    if (!shouldUpdateAdaptiveCard) {
      dispatch(addAdaptiveCardData(updatedAdaptiveCardToAdd));
    }
  } catch (error) {
    console.error('addAdaptiveCard', `${error}`);
  }
};